import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { Filters, mergeFilters } from "@/lib/filters";
import { ProjectStoreService } from "@/modules/projects/service/projectStore";
import { FetchListRequest } from "@/services/api";
import { DisplayProjectStore, ReadProjectStore } from "@/types/projectStore";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export const useProjectStoresQuery = ({
  projectId,
  enabled = true,
  filters: filtersFromProps,
  name = "projectStores",
}: {
  projectId: string;
  enabled?: boolean;
  filters?: Filters<ReadProjectStore>;
  name?: string;
}) => {
  const options = useCreateDataTableOptions<DisplayProjectStore>(name);
  const fetchList = (fetchParams: FetchListRequest<ReadProjectStore>) => {
    return ProjectStoreService.getList(fetchParams, `/${projectId}/stores`);
  };

  const filters = useMemo(
    () => mergeFilters(options.filters, filtersFromProps),
    [filtersFromProps, options.filters],
  );

  const queryKey = useDynamicQueryParams([name, projectId], options);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchList({
        lastItem: pageParam,
        filters,
        sorting: options.sorting[0],
      }),
    initialPageParam: undefined,
    getNextPageParam: ({ last_item }) => last_item,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled,
  });

  return {
    query,
    options,
  };
};
